<template>
  <div v-if="visible" class="config-dialog">
    <div class="dialog-content">
      <div class="dialog-header-inner">
        <div class="small-title">
          <span>{{ dialogTitle }}</span>
        </div>
      </div>
      <el-tabs v-if="hasDialogPermission" v-model="activeTab" class="dialog-tabs">
        <el-tab-pane label="恒星" name="stars"></el-tab-pane>
        <el-tab-pane label="容许度设置" name="phases"></el-tab-pane>
      </el-tabs>
      <div v-if="hasDialogPermission" class="dialog-body-content">
        <div v-if="activeTab === 'stars'">
          <div class="selection-actions">
            <el-checkbox
              :value="isAllSelected"
              :indeterminate="isIndeterminate"
              @change="toggleSelectAll"
            >
              全选
            </el-checkbox>
            <!-- 搜索框（2026-10-13）：紧挨「全选」右侧 20px（不靠最右侧），
                 只过滤**当前「恒星」页签**下方的按钮列表（按按钮名称模糊匹配）；
                 「容许度设置」页签不受影响；输入即过滤、清空即恢复全部。 -->
            <el-input
              v-model="searchKeyword"
              class="option-search"
              style="width:150px;margin-left:20px;"
              size="mini"
              clearable
              :placeholder="searchPlaceholder"
            />
          </div>
          <div class="options-container">
            <div class="option-item" v-for="star in visibleStars" :key="star.id">
              <button
                type="button"
                class="option-button"
                :class="{ 'active': star.selected }"
                @click="toggleStarSelected(star)"
              >
                <span class="option-label">{{ star.name }}</span>
                <!-- 按钮高亮 = 「参与请求」态（star.selected）：
                       - 受管控恒星（starControlStars.js 名单）恒为选中 → 按钮默认固定选中样式，
                         点击不会取消、也不需要动右侧确认框来改变样式；
                       - 普通恒星：点按钮选中=参与+展示，点按钮取消=不再展示；
                         取消只影响画布展示，**不会**把它移出后端请求参数（参与集合只增不减）。
                     右侧确认框只控制「画布展示」（取消勾选=透明隐藏，仍在请求里）。
                     例外：点「全选」会把全部并入请求；点「取消全选」可把请求收回
                     只剩受管控必选恒星（用于清除历史上被全选放大的请求参数）。 -->
                <el-checkbox
                  class="option-canvas-check"
                  :value="star.visibleOnCanvas === true"
                  @click.native.stop
                  @change="val => toggleCanvasVisible(star, val)"
                />
              </button>
            </div>
          </div>
          <!-- 搜索无结果时的空态：必须放在本页签块**内部**。
               绝不能插在 v-if 与 v-else 之间 —— 那会抢走 else 的配对，
               让「容许度设置」卡片在「恒星」页签里被一并渲染出来。 -->
          <div
            v-if="visibleStars.length === 0"
            style="padding:12px 4px;font-size:12px;color:#909399;"
          >
            没有匹配「{{ searchKeyword }}」的恒星，请更换关键字
          </div>
        </div>
        <div v-else class="phase-settings-card">
          <div class="options-container-grid">
            <div class="option-item" v-for="star in configurableLocalStars" :key="star.id">
              <label>
                <span class="option-name">{{ star.name }}：</span>
                <div class="dms-inputs">
                  <input
                    type="text"
                    v-model="star.degrees"
                    class="orb-input dms-input"
                    placeholder="度"
                    @input="validateInput(star, 'degrees')"
                  >
                  <span class="dms-separator">°</span>
                  <input
                    type="text"
                    v-model="star.minutes"
                    class="orb-input dms-input"
                    placeholder="分"
                    @input="validateInput(star, 'minutes')"
                  >
                  <span class="dms-separator">'</span>
                  <input
                    type="text"
                    v-model="star.seconds"
                    class="orb-input dms-input"
                    placeholder="秒"
                    @input="validateInput(star, 'seconds')"
                  >
                  <span class="dms-separator">"</span>
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>
      <div class="dialog-footer">
        <div class="dialog-buttons">
          <button class="btn-cancel" @click="cancel">
            取消
          </button>
          <button class="btn-confirm" @click="confirm">
            确定
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Message } from 'element-ui'
import PermissionService from '@/services/PermissionService'
// 恒星管控：受管控恒星的按钮/容许度行默认屏蔽，需开通「恒星管控」后才展示
const starControl = require('../../features/tianxing/utils/starControl')
// 「参与请求」集合（只增不减）：取消只影响画布展示，不影响请求参数与报警判定（2026-10-10）
const canvasParticipation = require('../../features/tianxing/utils/canvasParticipation')

// 按钮名称搜索过滤（只按名称子串匹配）
const nameSearchFilter = require('../../utils/nameSearchFilter')

export default {
  name: 'StarConfigDialog',
  props: {
    visible: Boolean,
    stars: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      activeTab: 'stars',
      localStars: [],
      backupStars: [],
      // 「参与请求」集合（2026-10-10，**只增不减**）：
      // 任何"取消"动作（取消按钮 / 取消勾选确认框 / 取消全选）都不会把恒星从请求里移除，
      // 因此被隐藏的恒星数据照常运行、撞到四轴照常参与点/面报警判定。
      participationIds: [],
      // 按钮名称搜索（2026-10-13）：只过滤当前页签的展示列表，
      // 不影响选中态/参与集合/请求参数；打开弹窗或切换页签时清空
      searchKeyword: ''
    }
  },
  computed: {
    dialogTitle () {
      if (this.activeTab === 'phases') {
        return '恒星容许度设置'
      } else {
        return '选择要显示的恒星'
      }
    },
    hasDialogPermission () {
      return PermissionService.hasPermission('full_stars')
    },
    /** 是否已开通「恒星管控」（与点报警/面报警同一通道，读登录态到期字段） */
    hasStarControl () {
      return starControl.isStarControlGrantedFromStorage()
    },
    // 2026-10-11 口径调整：权限管控前移到「弹窗入口」——
    // 未开通「恒星管控」的用户根本打不开本弹窗（入口判权在 AstrolabeCanvas 的
    // handleOpenStarConfig），因此弹窗内**不再按权限屏蔽**受管控恒星：
    // 17 颗指定恒星照常展示（容许度行同样照常展示），
    // 但保持原有设定不变：**固定选中、无法取消**（见 toggleStarSelected）。
    configurableStars () {
      return this.stars
    },
    configurableLocalStars () {
      return this.localStars
    },
    // ---- 按钮名称搜索（2026-10-13）----
    // 只过滤「恒星」页签的按钮列表（configurableStars），
    // 容许度设置页签的行不受影响；关键字为空即恢复全部。
    visibleStars () {
      return nameSearchFilter.filterOptions(this.configurableStars, this.searchKeyword)
    },
    searchPlaceholder () {
      return '搜索恒星名称'
    },
    // 「全选」勾选态 = 「参与请求」态（与按钮同一口径，2026-10-10 还原）
    isAllSelected () {
      return this.stars.length > 0 && this.stars.every(s => s.selected)
    },
    isIndeterminate () {
      const selectedCount = this.stars.filter(s => s.selected).length
      return selectedCount > 0 && selectedCount < this.stars.length
    }
  },
  watch: {
    // 打开弹窗清空关键字，避免上次过滤条件残留（否则会"一打开就少了一半"）
    visible (val) {
      if (val) {
        this.searchKeyword = ''
      }
    },
    // 切换页签（恒星 / 容许度设置）时清空：搜索只针对当前页签，切页后从"全部内容"开始
    activeTab () {
      this.searchKeyword = ''
    },
    stars: {
      immediate: true,
      deep: true,
      handler (newVal) {
        // 「参与请求」只增不减（2026-10-10）：把当前已选中的恒星并入参与集合，不做删除
        this.seedParticipation(newVal)
        this.localStars = JSON.parse(JSON.stringify(newVal))
        this.backupStars = JSON.parse(JSON.stringify(newVal))
        this.localStars.forEach(star => {
          this.floatToDMS(star)
        })
        this.backupStars.forEach(star => {
          this.floatToDMS(star)
        })
      }
    }
  },
  methods: {
    // 全选 / 取消全选（2026-10-10）：
    //   全选     → 全部并入「参与请求」集合（用户主动要全部）；
    //   取消全选 → 把参与集合**收回只剩受管控必选恒星**，用于清除历史上被"全选"
    //              放大的请求参数（否则请求会永久停在全量，无法收回）。
    toggleSelectAll (val) {
      this.stars.forEach(star => {
        const controlled = starControl.isControlledStar(star.id)
        // 受管控恒星恒为已选（口径：默认存在、无法取消）
        star.selected = controlled ? true : val
        // 受管控恒星的画布展示默认关闭；未开通「恒星管控」时**全选也不点亮**它们
        if (controlled && !this.hasStarControl) {
          this.$set(star, 'visibleOnCanvas', false)
          return
        }
        // 与单点按钮同一口径：全选即全部勾选画布展示，全不选即全部取消勾选
        this.$set(star, 'visibleOnCanvas', val === true)
      })
      this.participationIds = val === true
        ? this.stars.map(star => star.id)
        : canvasParticipation.onlyMandatoryIds(this.stars, star => starControl.isControlledStar(star.id))
    },
    // 恒星按钮点击：切换选中状态，并与「画布展示」确认框联动（2026-10-09 调整）。
    //   选中按钮   → 连带勾选确认框（与历史行为一致）；
    //   取消按钮   → 连带取消勾选确认框（本次新增）。
    // 反向约定：勾选确认框会连带选中按钮；取消勾选确认框不影响按钮。
    // 2026-10-10：无论选中还是取消，都只影响「画布展示」；已出现过的恒星恒留在请求里。
    toggleStarSelected (star) {
      if (!star) return
      // 受管控恒星恒为已选：点击按钮不改变选中状态（无法取消）。
      // 画布展示改由复选框控制（且未开通「恒星管控」时不允许点亮）。
      if (starControl.isControlledStar(star.id)) {
        star.selected = true
        this.markParticipation(star.id)
        return
      }
      star.selected = !star.selected
      this.$set(star, 'visibleOnCanvas', star.selected === true)
      if (star.selected === true) {
        this.markParticipation(star.id)
      }
    },
    // 画布展示确认框：勾选=画布绘制该恒星，取消勾选=停止绘制。
    //   勾选   → 连带把该恒星按钮选上（配合"要展示就得先选中"的口径）；
    //   取消勾选 → 只改画布展示状态，不修改 star.selected，也不触发其它状态变更。
    // 2026-10-10：取消勾选**不会**把恒星移出请求，数据照常运行、照常参与报警判定。
    toggleCanvasVisible (star, visible) {
      if (!star) return
      // 未开通「恒星管控」时，受管控恒星不允许被点亮到画布（保持默认隐藏）
      if (starControl.isControlledStar(star.id) && !this.hasStarControl) {
        this.$set(star, 'visibleOnCanvas', false)
        return
      }
      this.$set(star, 'visibleOnCanvas', visible === true)
      if (visible === true) {
        star.selected = true
        // 只有"要展示"才需要并入请求（展示依赖后端返回的数据）；
        // 取消勾选只是停止绘制，既不移出请求、也不新增请求项
        this.markParticipation(star.id)
      }
    },
    // 记录「参与请求」的对象 id（**只增不减**，永不删除）
    markParticipation (id) {
      this.participationIds = canvasParticipation.markParticipation(this.participationIds, id)
    },
    // 把列表里"当前已选中"的对象并入参与集合（并集，不做删除）
    seedParticipation (list) {
      this.participationIds = canvasParticipation.seedParticipation(this.participationIds, list)
    },
    // 提交给宿主的请求对象列表 = 参与集合 ∪ 当前选中（保证"取消"不会减少请求参数）
    resolveRequestIds () {
      return canvasParticipation.resolveRequestIds(this.participationIds, this.stars)
    },
    confirm () {
      if (!this.hasDialogPermission) {
        this.$emit('update:visible', false)
        return
      }

      // 容许度「不能全为 0」（0°0'0"）：该值在整条链路里等价于「未配置」——
      // 落库时被 buildStarToleranceMap 丢弃、星盘侧被 StarToleranceContext 过滤后回落 1.0，
      // 静默放行会让用户以为设成了 0、实际仍是 1 度。故显式拦下并定位到具体恒星：
      // 不关闭弹窗、不提交、也不触发生成星盘，用户改完可再点「确定」。
      // 必须放在 update:matrix / save 之前 —— 宿主收到 save 会立刻用新容许度重算星盘。
      const zeroStar = this.localStars.find(star => this.isZeroDms(star))
      if (zeroStar) {
        Message.warning(`「${zeroStar.name}」的容许度不能全为 0，请至少填写 1 分或 1 秒`)
        this.activeTab = 'phases'
        return
      }

      // 容许度必须先于 save 提交：宿主收到 save 会立刻重新生成星盘，
      // 而恒星容许度是随星盘请求一起下发的（starToleranceMap）。
      // 若顺序反过来，这一次生成用的仍是旧容许度，用户改完看不到报警变化，
      // 必须等下一次换盘/刷新才生效。
      const starsWithFloat = this.localStars.map(star => {
        const starCopy = JSON.parse(JSON.stringify(star))
        this.dmsToFloat(starCopy)
        return starCopy
      })
      this.$emit('update:matrix', starsWithFloat)

      // 「参与请求」只增不减（2026-10-10）：提交"参与集合 ∪ 当前选中"，
      // 保证取消按钮 / 取消勾选确认框只影响画布展示，不影响请求参数与报警判定。
      const selectedIds = this.resolveRequestIds()
      this.$emit('save', selectedIds)

      // 画布展示：把「确认框未勾选」的恒星 id 告知画布。
      // 画布只据此停止绘制这些恒星，选中列表（selectedIds）保持原样，
      // 因此请求参数、容许度等其它方面都不受影响。
      const canvasHiddenIds = this.stars
        .filter(s => s.visibleOnCanvas !== true)
        .map(s => s.id)
      this.$emit('update-canvas-visible', canvasHiddenIds)

      this.$emit('update:visible', false)
    },
    cancel () {
      this.$emit('update:visible', false)
    },
    /**
     * 解析一个度 / 分 / 秒输入格。
     *
     * 与历史写法的关键差别：**显式填 0 必须保留为 0**。
     * 旧写法 `parseFloat(value) || 1` 把 0 当成 falsy 直接变 1，导致界面永远做不出
     * 小于 1 度的容许度（例如 0°30' = 0.5）。这里只有「空着 / 非数字」才回落默认值，
     * 所以「清空输入框」与「真的敲了 0」是两件事：
     * 清空 = 该项用默认值（度 1、分 0、秒 0），敲 0 = 就是 0。
     */
    parseDmsPart (value, fallback) {
      if (value === undefined || value === null || String(value).trim() === '') {
        return fallback
      }
      const parsed = Number(value)
      return Number.isFinite(parsed) ? parsed : fallback
    },
    /**
     * 度分秒 → 度（不修改入参），校验与提交共用同一口径。
     *
     * 度数可小到 0：配合分/秒即可表达小于 1 度的容许度（界面可达下限 0°0'1" ≈ 0.000278°）。
     * 但「度分秒全为 0」不允许 —— 0 度在链路里等价于「未配置」（落库被丢弃、星盘回落 1.0），
     * 由 {@link confirm} 显式拦下。
     */
    dmsToDegrees (star) {
      const degrees = this.parseDmsPart(star.degrees, 1) // 空着不填 → 默认 1 度（与历史一致）
      const minutes = this.parseDmsPart(star.minutes, 0)
      const seconds = this.parseDmsPart(star.seconds, 0)
      return degrees + (minutes / 60) + (seconds / 3600)
    },
    /** 换算后的容许度是否为 0（等价于度分秒全为 0），与提交口径完全同源 */
    isZeroDms (star) {
      return this.dmsToDegrees(star) === 0
    },
    dmsToFloat (star) {
      star.tolerance = this.dmsToDegrees(star)
      return star.tolerance
    },
    floatToDMS (star) {
      // 默认容许度为1度
      const defaultTolerance = 1.0
      const tolerance = parseFloat(star.tolerance)
      // 如果容许度为 NaN 或 undefined，使用默认值1度
      const validTolerance = isNaN(tolerance) ? defaultTolerance : tolerance

      let degrees = Math.floor(validTolerance)
      const remaining = validTolerance - degrees
      let minutes = Math.floor(remaining * 60)
      let seconds = Math.round(((remaining * 60) - minutes) * 60)

      // 秒位四舍五入可能进位到 60（如 0.99999° → 0°59'60"）：显示出来不合法，
      // 且再次点「确定」时会把 59'60" 当成 1.0，值被悄悄放大。故逐级进位，
      // 保证输出恒为合法的 度 / 分<60 / 秒<60。
      if (seconds >= 60) {
        seconds = 0
        minutes += 1
      }
      if (minutes >= 60) {
        minutes = 0
        degrees += 1
      }

      star.degrees = degrees.toString()
      star.minutes = minutes.toString()
      star.seconds = seconds.toString()
    },
    validateInput (star, field) {
      let value = star[field]
      value = value.replace(/[^0-9]/g, '')
      let numValue = parseInt(value) || 0
      if (field === 'minutes' || field === 'seconds') {
        if (numValue >= 60) {
          numValue = 59
          value = '59'
        }
      }
      star[field] = value
    }
  }
}
</script>

<style scoped>
.dialog-content {
  width: 940px;
  max-height: 90vh;
  background: white;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
}

.dialog-header-inner {
  background: #f0f4f8;
  border-bottom: 1px solid #e2e8f0;
  padding: 8px 24px;
}

.small-title {
  font-size: 1.1rem;
  font-weight: 500;
  color: #4a5568;
  padding: 4px 0;
}

.dialog-body-content {
  padding: 0px 24px;
  flex: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
}

.selection-actions {
  padding: 12px 0 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid #edf2f7;
  display: flex;
  align-items: center;
}

.selection-actions >>> .el-checkbox__label {
  font-size: 0.9rem;
  color: #4a5568;
}

.options-container {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
}

.option-item {
  display: flex;
  align-items: center;
  justify-content: center;
}

.dialog-tabs {
  padding: 12px 24px 0;
}

.dialog-tabs >>> .el-tabs__nav {
  border: none;
  margin-bottom: -1px;
  display: flex;
  flex-wrap: nowrap;
}

.dialog-tabs >>> .el-tabs__item {
  width: 150px;
  height: 40px;
  padding: 0;
  margin-right: 4px;
  font-size: 0.9rem;
  color: #4a5568;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-bottom: none;
  border-radius: 6px 6px 0 0;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  box-sizing: border-box;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}

.dialog-tabs >>> .el-tabs__item:hover {
  color: #3182ce;
  border-color: #bee3f8;
}

.dialog-tabs >>> .el-tabs__item.is-active {
  color: #3182ce;
  font-weight: 600;
  background: white;
  border-bottom: 1px solid white;
  margin-bottom: -1px;
}

.dialog-tabs >>> .el-tabs__active-bar {
  display: none;
}

.phase-settings-card {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.card-header {
  font-size: 1rem;
  font-weight: 500;
  color: #2d3748;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e2e8f0;
}

.options-container-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  padding: 0;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}

.phase-settings-card .option-item {
  background: #f8f9fa;
  border-radius: 0;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  border: 1px solid #e2e8f0;
  border-width: 0 1px 1px 0;
}

.phase-settings-card .options-container-grid .option-item:nth-child(4n) {
  border-right-width: 0;
}

.phase-settings-card .options-container-grid .option-item:nth-child(-n+4) {
  border-top-width: 0;
}

.phase-settings-card .option-item label {
  display: flex;
  align-items: center;
  gap: 0px;
  cursor: pointer;
}

.option-name {
  font-weight: 400;
  font-size: 0.8rem;
}

.orb-input {
  padding: 2px;
  border: 1px solid #cbd5e0;
  border-radius: 4px;
  text-align: center;
}

.dms-inputs {
  display: flex;
  align-items: center;
  gap: 1px;
  margin-left: auto;
}

.dms-input {
  width: 28px;
}

.dms-separator {
  font-size: 0.7rem;
  color: #718096;
  font-weight: 500;
  margin: 0 1px;
}

/* 按钮基础样式 - 尺寸更小 */
.option-button {
  width: 100%;
  min-height: 32px;
  padding: 4px 8px;
  border: 1px solid #cbd5e0;
  border-radius: 4px;
  background: #f7fafc;
  color: #4a5568;
  font-size: 0.8rem;
  font-weight: 400;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  word-break: break-word;
  white-space: normal;
}

/* 未选中状态 - 灰色 */
.option-button:not(.active) {
  background: #f7fafc;
  color: #718096;
  border-color: #cbd5e0;
}

/* 选中状态 - 高亮蓝色 */
.option-button.active {
  background: #3182ce;
  color: white;
  border-color: #3182ce;
  box-shadow: 0 1px 3px rgba(49, 130, 206, 0.3);
}

/* 悬停效果 */
.option-button:hover:not(.active) {
  background: #edf2f7;
  border-color: #a0aec0;
}

.option-button.active:hover {
  background: #2c5aa0;
  border-color: #2c5aa0;
}

/* 按钮内文字：占满剩余宽度并居中；右侧固定放「画布展示」确认框 */
.option-label {
  flex: 1;
  text-align: center;
  padding-left: 16px; /* 左侧补偿确认框宽度，保持文字视觉居中 */
}

/* 按钮内的画布展示确认框 */
.option-canvas-check {
  margin-right: 6px;
  flex-shrink: 0;
}

/* 蓝色选中态下确认框用白底蓝勾，保证在蓝底上可见 */
.option-button.active .option-canvas-check >>> .el-checkbox__inner {
  background-color: #ffffff;
  border-color: #ffffff;
}

.option-button.active .option-canvas-check >>> .el-checkbox__inner::after {
  border-color: #3182ce;
}

.permission-guard {
  margin: 18px 24px;
  padding: 16px 18px;
  border-radius: 8px;
  background: #fff7e6;
  border: 1px solid #f5c98a;
  color: #8a5a12;
  font-size: 14px;
}

.dialog-footer {
  padding: 8px 24px;
  background: #f8f9fa;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
}

.dialog-buttons {
  display: flex;
  gap: 12px;
}

.btn-confirm, .btn-cancel {
  padding: 6px 14px;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
}

.btn-confirm {
  background: #3182ce;
  color: white;
  border: 1px solid #3182ce;
}

.btn-cancel {
  background: #f7fafc;
  color: #4a5568;
  border: 1px solid #cbd5e0;
}

@media (max-width: 850px) {
  .options-container {
    grid-template-columns: repeat(4, 1fr);
  }

  .dialog-content {
    width: 95%;
    height: 85vh;
  }
}

@media (max-width: 600px) {
  .options-container {
    grid-template-columns: repeat(3, 1fr);
  }

  .options-container-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .option-button {
    min-height: 28px;
    font-size: 0.75rem;
    padding: 3px 6px;
  }

  .dms-inputs {
    gap: 1px;
  }

  .dms-input {
    width: 22px;
  }

  .dms-separator {
    font-size: 0.6rem;
  }

  .option-name {
    font-size: 0.6rem;
  }
}
</style>
